/**
 * Canonical post-auth and cross-host routing for agency vs platform/staff users.
 */

import {
  AGENCY_APP_URL,
  PLATFORM_APP_URL,
  FUNDGENIUS_APP_URL,
  HOSTNAME_PORTALS,
  getHostnamePortal,
  isPlatformConsoleRole,
  isNonProductionAppHost,
  isPreviewDeploymentHost,
} from './hostnamePortal';
import {
  getClientPortalDashboardPath,
  hasValidAgencyId,
  isAgencyUser,
  isClientPortalUser,
  isPlatformAdmin,
} from './portalAccess';
import {
  FUNDELITE_PLATFORM_LANDING,
  getPortalLandingRoute,
  isFundElitePlatformLandingHost,
  isFundGeniusLandingHost,
  isFundGeniusPath,
  isPlatformAgenciesListPath,
  rejectAgenciesAsDefault,
} from './portalLandingRoute';
import { resolveAuthenticatedHome } from './authenticatedHome';

export {
  getClientPortalDashboardPath,
  hasAgencyAccess,
  hasValidAgencyId,
  isAgencyUser,
  isClientPortalUser,
  isManagedClientUser,
  isPlatformAdmin,
  isSelfRepairUser,
} from './portalAccess';

export const AGENCY_DASHBOARD_PATH = '/agency/dashboard';

/** Auth routes that must never trigger cross-host redirects in HostnameRouteGate. */
export const HOST_SAFE_AUTH_PATHS = [
  '/login',
  '/signin',
  '/signup',
  '/choose-account',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/qa-diagnostics',
];

const HQ_STAFF_ROLES = new Set([
  'admin',
  'owner',
  'staff',
  'staff_admin',
  'support',
  'specialist',
]);

export function isHostSafeAuthPath(pathname = '') {
  const path = String(pathname || '');
  return HOST_SAFE_AUTH_PATHS.some(
    (route) => path === route || path.startsWith(`${route}/`)
  );
}

/**
 * True when user belongs on the agency product app (app.fundelite.ai).
 * Client/self-repair users are never agency workspace users, even with agency_id.
 */
export function isAgencyWorkspaceUser(user) {
  return isAgencyUser(user);
}

/** True when user belongs on platform HQ (app.fundelitehq.com). */
export function isPlatformStaffUser(user) {
  if (!user || isAgencyWorkspaceUser(user) || isClientPortalUser(user)) return false;

  const role = String(user?.role || '').toLowerCase();
  if (isPlatformAdmin(user)) return true;
  if (isPlatformConsoleRole(role)) return true;
  return HQ_STAFF_ROLES.has(role);
}

/** @deprecated Use isAgencyWorkspaceUser */
export const isAgencyOwnerUser = isAgencyWorkspaceUser;

export function agencyOwnerNeedsEmailVerification(user) {
  if (!user || !isAgencyWorkspaceUser(user)) return false;
  if (user.email_verified === true) return false;
  if (user.requires_email_verification === false) return false;
  return user.requires_email_verification === true || user.email_verified === false;
}

/**
 * Single canonical dashboard path resolver.
 * Hostname owns the root landing (FundElite vs FundGenius); roles refine within host.
 */
export function getDashboardForUser(user, fallbackRedirectTo = null) {
  if (!user) return '/login';
  if (
    user.requires_account_selection
    || user.pending_account_selection
    || user.account_selection_required
  ) {
    return '/choose-account';
  }
  if (agencyOwnerNeedsEmailVerification(user)) return '/verify-email';

  // Canonical account-context redirect (membership-aware) wins when present.
  // Never infer portal from role/localStorage before a scoped session exists.
  if (user.redirect_to && String(user.redirect_to).startsWith('/')) {
    return String(user.redirect_to);
  }
  if (
    user.portal
    || user.active_account
    || user.selected_account
    || user.effective_role
  ) {
    const home = resolveAuthenticatedHome(user);
    if (home && home !== '/unauthorized') {
      return home;
    }
  }

  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';
  const portal = getHostnamePortal(hostname);
  const fallback =
    fallbackRedirectTo && String(fallbackRedirectTo).startsWith('/')
      ? String(fallbackRedirectTo)
      : null;

  // FundGenius app host: hostname-owned landing (never FundElite dashboard).
  if (isFundGeniusLandingHost(hostname) || portal === HOSTNAME_PORTALS.FUNDGENIUS_APP) {
    return getPortalLandingRoute({ hostname, user });
  }

  // FundElite platform host: never inherit FundGenius or Agencies as default landing.
  // Explicit same-portal return URLs (including /platform/agencies) remain allowed.
  if (portal === HOSTNAME_PORTALS.ADMIN || isFundElitePlatformLandingHost(hostname)) {
    if (
      fallback
      && fallback !== '/'
      && fallback !== '/login'
      && fallback !== '/signin'
      && !isFundGeniusPath(fallback)
    ) {
      return fallback;
    }
    return rejectAgenciesAsDefault(getPortalLandingRoute({ hostname, user }));
  }

  if (fallback && fallback !== '/' && fallback !== '/login' && fallback !== '/signin') {
    return fallback;
  }

  const landing = getPortalLandingRoute({ hostname, user });
  // Never promote Agencies list as an implicit first-allowed fallback.
  if (isPlatformAgenciesListPath(landing)) {
    return FUNDELITE_PLATFORM_LANDING;
  }
  return landing;
}

function joinUrl(base, path) {
  const normalizedBase = String(base || '').replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
}

export function logRouteResolver(user, targetUrl, pathname = null) {
  if (process.env.NODE_ENV === 'production') return;
  console.info('[RouteResolver]', {
    currentHost: typeof window !== 'undefined' ? window.location.hostname : null,
    path: pathname ?? (typeof window !== 'undefined' ? window.location.pathname : null),
    role: user?.role || null,
    agency_id: hasValidAgencyId(user) ? 'yes' : 'no',
    platform_access: user?.platform_access ?? null,
    targetUrl,
  });
}

/** Resolve path + full URL + whether a cross-host redirect is required. */
export function resolvePostAuthTarget(user, pathOverride = null) {
  const path = pathOverride || getDashboardForUser(user);
  const currentPortal = getHostnamePortal();

  // Preview / local DEV: never bounce to production app.fundelite.ai.
  // All post-login navigation stays on the current origin (relative Router paths).
  if (
    currentPortal === HOSTNAME_PORTALS.DEV
    || isPreviewDeploymentHost()
    || isNonProductionAppHost()
  ) {
    const host =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : AGENCY_APP_URL;
    const fullUrl = joinUrl(host, path);
    logRouteResolver(user, fullUrl, path);
    return { path, fullUrl, crossHost: false };
  }

  // Critical: on app.fundgeniushq.com never bounce agency owners to app.fundelite.ai.
  // Always navigate host-relative on the FundGenius app portal.
  if (currentPortal === HOSTNAME_PORTALS.FUNDGENIUS_APP) {
    const host =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : FUNDGENIUS_APP_URL;
    const fullUrl = joinUrl(host, path);
    logRouteResolver(user, fullUrl, path);
    return { path, fullUrl, crossHost: false };
  }

  let host = AGENCY_APP_URL;

  if (
    path.startsWith('/fundgenius-hq')
    || path.startsWith('/platform/fundgenius-hq')
  ) {
    host = FUNDGENIUS_APP_URL;
  } else if (isAgencyWorkspaceUser(user)) {
    host = AGENCY_APP_URL;
  } else if (isPlatformStaffUser(user) || isPlatformConsoleRole(user?.role)) {
    host = PLATFORM_APP_URL;
  } else if (typeof window !== 'undefined') {
    host = window.location.origin;
  }

  const fullUrl = joinUrl(host, path);
  const crossHost =
    typeof window !== 'undefined' &&
    new URL(fullUrl).origin !== window.location.origin;

  logRouteResolver(user, fullUrl, path);
  return { path, fullUrl, crossHost };
}

export function isSameNavigationTarget(fullUrl) {
  if (typeof window === 'undefined' || !fullUrl) return true;
  const current = window.location.href.split('#')[0];
  const target = String(fullUrl).split('#')[0];
  return current === target;
}

/** Navigate or cross-host redirect with loop prevention. */
export function applyPostAuthRedirect(user, navigate, pathOverride = null) {
  const target = resolvePostAuthTarget(user, pathOverride);
  if (isSameNavigationTarget(target.fullUrl)) {
    return target;
  }
  // Preview / DEV: always React Router — never window.location to production.
  if (
    !target.crossHost
    || isPreviewDeploymentHost()
    || isNonProductionAppHost()
    || getHostnamePortal() === HOSTNAME_PORTALS.DEV
  ) {
    navigate(target.path, { replace: true });
    return target;
  }
  if (target.crossHost) {
    window.location.replace(target.fullUrl);
  } else {
    navigate(target.path, { replace: true });
  }
  return target;
}

/**
 * Hostname gate: return full URL when user is on wrong host, else null.
 * Never redirects public/auth routes across hosts — login stays on the current host.
 * Preview / DEV hosts never receive production boundary redirects.
 */
export function resolvePortalBoundaryRedirect(
  user,
  { pathname = '/', search = '', portal = getHostnamePortal(), isInSupportMode = false } = {}
) {
  if (
    !user
    || portal === HOSTNAME_PORTALS.DEV
    || portal === HOSTNAME_PORTALS.MARKETING
    || isPreviewDeploymentHost()
    || isNonProductionAppHost()
  ) {
    return null;
  }

  if (isHostSafeAuthPath(pathname)) {
    return null;
  }

  if (isAgencyWorkspaceUser(user)) {
    if (portal === HOSTNAME_PORTALS.ADMIN) {
      const agencyPath = pathname.startsWith('/agency')
        ? pathname
        : AGENCY_DASHBOARD_PATH;
      return joinUrl(AGENCY_APP_URL, agencyPath);
    }
    return null;
  }

  if (isPlatformStaffUser(user) && !isInSupportMode && portal === HOSTNAME_PORTALS.CUSTOMER) {
    const staffPath = getDashboardForUser(user);
    return joinUrl(PLATFORM_APP_URL, staffPath);
  }

  return null;
}
